"use client"

import hotkeys, { HotkeysEvent, KeyHandler } from "hotkeys-js"
import { useMounted } from "./useMounted"
import { useLayoutEffect } from "react"
import { useMemoizedFn } from "ahooks"

export interface UseHotkeyOptions {
  /**
   * If true, the hotkey will be triggered even if the event is already handled by another hotkey. This is useful for global hotkeys that should work regardless of the context.
   */
  phantom?: boolean
  /**
   * By default hotkey is available on mount, if getIsActive is provided,
   * result will be used to register/unregister hotkey.
   */
  getIsActive?: () => boolean
  /**
   * Allows shortcut to work while focused on input, textarea or select elements.
   */
  input?: boolean
}

/**
 *```ts
 * A stack aware hotkey register hook. Only last registered hotkey runs.
 * useHotkey("cmd+z", () => {})
 * // With custom condition
 * useHotkey("esc", () => {}, { isActive: () => modalVisible })
 *
 * // Phantom hotkey: They are ignored from stack and always run. Useful for global hotkeys.
 * useHotkey("cmd+z,ctrl+z", () => {}, { phantom: true })
 * ```
 */
export function useHotkey(
  hotkey: string,
  handler: KeyHandler,
  options: UseHotkeyOptions = {},
) {
  const hotkeyHandler = getHotkeyHandler()
  const { getIsActive, phantom = false, input = false } = options

  const mounted = useMounted()
  const active =
    typeof getIsActive === "function" ? Boolean(getIsActive()) : mounted

  const hotkeyFn = useMemoizedFn(
    (event: KeyboardEvent, hotkeysEvent: HotkeysEvent) => {
      if (
        !input &&
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA" ||
          event.target.tagName === "SELECT" ||
          event.target.isContentEditable)
      ) {
        return
      }

      handler(event, hotkeysEvent)
    },
  )

  useLayoutEffect(() => {
    const keys = hotkey.split(",").map((h) => h.trim())

    if (active) {
      for (const h of keys) {
        hotkeyHandler.register(h, hotkeyFn, phantom)
      }
    }

    return () => {
      for (const h of keys) {
        hotkeyHandler.unregister(h, hotkeyFn)
      }
    }
  }, [active, hotkey, hotkeyFn, hotkeyHandler, phantom])
}

interface HotkeyHandler {
  map: Record<
    string,
    {
      fn: KeyHandler
      phantom: boolean
    }[]
  >
  register: (hotkey: string, handler: KeyHandler, phantom?: boolean) => void
  unregister: (hotkey: string, handler: KeyHandler) => void
}

declare global {
  var __hotkeyHandler: HotkeyHandler
}

function createHotkeyHandler(): HotkeyHandler {
  const map = Object.create(null) as HotkeyHandler["map"]
  const k = hotkeys.noConflict(true)

  k.filter = () => true

  function getHandlers(hotkey: string) {
    const phantomHandlers = [] as KeyHandler[]
    let handler = null as KeyHandler | null

    for (const item of (map[hotkey] ?? []).toReversed()) {
      if (item.phantom) {
        phantomHandlers.push(item.fn)
        continue
      }

      handler ??= item.fn
    }

    return [...(handler ? [handler] : []), ...phantomHandlers]
  }

  function register(hotkey: string, handler: KeyHandler, phantom = false) {
    map[hotkey] ??= []

    if (map[hotkey].length === 0) {
      k(hotkey, { capture: true }, (event, hotkeysEvent) => {
        if (event.repeat) return

        const handlers = getHandlers(hotkey)
        for (const fn of handlers) {
          fn(event, hotkeysEvent)
        }
      })
    }

    map[hotkey].push({
      fn: handler,
      phantom,
    })
  }

  function unregister(hotkey: string, handler: KeyHandler) {
    map[hotkey] ??= []
    map[hotkey] = map[hotkey].filter((h) => h.fn !== handler)

    if (map[hotkey].length === 0) {
      k.unbind(hotkey)
    }
  }

  return {
    map,
    register,
    unregister,
  }
}

function getHotkeyHandler() {
  globalThis.__hotkeyHandler ??= createHotkeyHandler()
  return globalThis.__hotkeyHandler
}
